import React, { forwardRef, useState } from 'react';
import { CustomModal } from '@components';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { apiCreateMarketBidAsk, apiGetMarketplace } from '@store';
import { showToast } from '@utils';

const CreateTrade = forwardRef(({ handleClose, contracts }, ref) => {
    const dispatch = useDispatch();
    const initialState = {
        contract: '',
        price: '',
        quantity: '',
        ttl: '',
        type: '',
        location: 'SEA',
        currency: '',
        option: ''
    };

    const [state, setState] = useState(initialState);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        // Validate contract
        if (!state.contract || state.contract.trim() === '') {
            newErrors.contract = 'Contract is required';
        }

        // Validate price
        if (!state.price || state.price === '') {
            newErrors.price = 'Price is required';
        } else if (parseFloat(state.price) <= 0) {
            newErrors.price = 'Price must be greater than 0';
        }

        // Validate quantity
        if (!state.quantity || state.quantity === '') {
            newErrors.quantity = 'Max Volume is required';
        } else if (parseFloat(state.quantity) <= 0) {
            newErrors.quantity = 'Max Volume must be greater than 0';
        }

        // Validate TTL - must be greater than 1
        if (!state.ttl || state.ttl === '') {
            newErrors.ttl = 'TTL is required';
        } else if (parseFloat(state.ttl) <= 1) {
            newErrors.ttl = 'TTL must be greater than 1';
        }

        // Validate type (Bid/Ask)
        if (!state.type || state.type.trim() === '') {
            newErrors.type = 'Bid/Ask type is required';
        }

        // Validate option
        if (!state.option || state.option.trim() === '') {
            newErrors.option = 'Order type is required';
        }

        // Validate currency
        if (!state.currency || state.currency.trim() === '') {
            newErrors.currency = 'Currency is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isFormValid = () => {
        return (
            state.contract &&
            state.price &&
            parseFloat(state.price) > 0 &&
            state.quantity &&
            parseFloat(state.quantity) > 0 &&
            state.ttl &&
            parseFloat(state.ttl) > 1 &&
            state.type &&
            state.option &&
            state.currency
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState({
            ...state,
            [name]: value
        });
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const newErrors = { ...errors };

        if (name === 'price') {
            if (!value || value === '') {
                newErrors.price = 'Price is required';
            } else if (parseFloat(value) <= 0) {
                newErrors.price = 'Price must be greater than 0';
            } else {
                newErrors.price = '';
            }
        }

        if (name === 'quantity') {
            if (!value || value === '') {
                newErrors.quantity = 'Max Volume is required';
            } else if (parseFloat(value) <= 0) {
                newErrors.quantity = 'Max Volume must be greater than 0';
            } else {
                newErrors.quantity = '';
            }
        }

        if (name === 'ttl') {
            if (!value || value === '') {
                newErrors.ttl = 'TTL is required';
            } else if (parseFloat(value) <= 1) {
                newErrors.ttl = 'TTL must be greater than 1';
            } else {
                newErrors.ttl = '';
            }
        }

        if (name === 'contract') {
            newErrors.contract =
                !value || value.trim() === '' ? 'Contract is required' : '';
        }

        if (name === 'type') {
            newErrors.type =
                !value || value.trim() === '' ? 'Bid/Ask type is required' : '';
        }

        if (name === 'option') {
            newErrors.option =
                !value || value.trim() === '' ? 'Order type is required' : '';
        }

        if (name === 'currency') {
            newErrors.currency =
                !value || value.trim() === '' ? 'Currency is required' : '';
        }

        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            showToast('error', 'Please fill all required fields correctly');
            return;
        }

        const data = {
            ...state,
            price: parseFloat(state.price),
            quantity: parseFloat(state.quantity),
            ttl: parseFloat(state.ttl)
        };

        const resp = await dispatch(apiCreateMarketBidAsk(data));
        if (resp?.meta?.requestStatus === 'fulfilled') {
            showToast('success', 'Trade created successfully');
            dispatch(apiGetMarketplace());
            handleClose();
        } else if (resp?.meta?.requestStatus === 'rejected') {
            showToast('error', resp?.payload || 'Failed to create trade');
        }
    };

    return (
        <CustomModal
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            modalTitle={`Create New Trade`}
            buttonText={'Submit'}
            disabled={!isFormValid()}
            ref={ref}
        >
            <Box className="flex gap-3">
                <Box className="flex flex-col gap-2">
                    <FormControl error={!!errors.contract}>
                        <InputLabel id="demo-simple-select-label">
                            Contract
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Contract"
                            name="contract"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state?.contract}
                            required
                        >
                            {contracts?.map((name, index) => (
                                <MenuItem key={index} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.contract && (
                            <span className="text-red-500 text-xs">
                                {errors.contract}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.price}>
                        <InputLabel id="priceLabel">Price</InputLabel>
                        <OutlinedInput
                            id="price"
                            label="Price"
                            name="price"
                            type="number"
                            //non negative and non zero number
                            inputProps={{ min: 0, step: 'any' }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.price}
                        />
                        {errors.price && (
                            <span className="text-red-500 text-xs">
                                {errors.price}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.type}>
                        <InputLabel id="demo-simple-select-label">
                            Bid/Ask
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="bid/ask"
                            name="type"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.type}
                        >
                            <MenuItem value="bid">Bid</MenuItem>
                            <MenuItem value="ask">Ask</MenuItem>
                        </Select>
                        {errors.type && (
                            <span className="text-red-500 text-xs">
                                {errors.type}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.option}>
                        <InputLabel id="demo-simple-select-label">
                            Type
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="bid/ask"
                            name="option"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.option}
                        >
                            <MenuItem value="AON">All Or None</MenuItem>
                            <MenuItem value="POK">Partial Fill Ok</MenuItem>
                        </Select>
                        {errors.option && (
                            <span className="text-red-500 text-xs">
                                {errors.option}
                            </span>
                        )}
                    </FormControl>
                </Box>
                <Box className="flex flex-col gap-2">
                    <FormControl error={!!errors.quantity}>
                        <InputLabel id="maxVolumeLabel">
                            Max Volume [MWh]
                        </InputLabel>
                        <OutlinedInput
                            id="max_volume"
                            label="Max Volume [MWh]"
                            name="quantity"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.quantity}
                            inputProps={{ min: 0, step: 'any' }}
                        />
                        {errors.quantity && (
                            <span className="text-red-500 text-xs">
                                {errors.quantity}
                            </span>
                        )}
                    </FormControl>
                    <FormControl error={!!errors.ttl}>
                        <InputLabel id="maxVolumeLabel">
                            TTL (Time To Live) In Minutes
                        </InputLabel>
                        <OutlinedInput
                            id="ttl"
                            label="TTL (Time To Live) In Minutes"
                            name="ttl"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.ttl}
                        />
                        {errors.ttl && (
                            <span className="text-red-500 text-xs">
                                {errors.ttl}
                            </span>
                        )}
                    </FormControl>
                    <FormControl>
                        <InputLabel id="demo-simple-select-label">
                            Location
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="location"
                            name="location"
                            onChange={handleChange}
                            value={state.location}
                        >
                            <MenuItem value="SEA">SEA</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl error={!!errors.currency}>
                        <InputLabel id="demo-simple-select-label">
                            Currency
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="currency"
                            name="currency"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={state.currency}
                        >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="CHF">CHF</MenuItem>
                        </Select>
                        {errors.currency && (
                            <span className="text-red-500 text-xs">
                                {errors.currency}
                            </span>
                        )}
                    </FormControl>
                </Box>
            </Box>
        </CustomModal>
    );
});

export default CreateTrade;
